import { Box, Typography } from "@mui/material"
import type { FC } from "react"

import { useInvoiceDocumentDelete } from "../../hooks/useInvoiceDocumentDelete"
import { useInvoiceDocuments } from "../../hooks/useInvoiceDocuments"
import { useInvoiceFilesColumns } from "../../hooks/useInvoiceFilesColumns"

import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import { FilesTable } from "@/components/Tables/Aeros/components/FilesTable"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"

interface InvoiceDocumentsProps {
  invoiceId: string
  canDelete?: boolean
}

export const InvoiceDocuments: FC<InvoiceDocumentsProps> = ({ invoiceId, canDelete = false }) => {
  const { documents, isFetching, refetch } = useInvoiceDocuments(invoiceId)
  const { hasPermissions } = usePermissionManager()
  const hasMaintenancePermission = canDelete && hasPermissions([PermissionActionType.ACCOUNTS_PAYABLE_MAINTENANCE])

  const { isConfirmationOpen, isDeleting, handleDeleteRequest, handleDeleteCancel, handleDeleteConfirm } =
    useInvoiceDocumentDelete(refetch)

  const columns = useInvoiceFilesColumns({ onDelete: hasMaintenancePermission ? handleDeleteRequest : undefined })

  return (
    <Box className='flex flex-1 flex-col gap-3 p-4' data-testid='invoices.documents-pane.container'>
      <Typography variant='h6' className='text-gray-900'>
        Documents
      </Typography>
      <FilesTable
        data={documents}
        columns={columns}
        isLoading={isFetching}
        noDataMessage='No documents have been added yet'
      />
      {isConfirmationOpen && (
        <ConfirmationDialog
          title='Remove Document'
          content='Are you sure you want to remove this document? This action cannot be undone.'
          closeLabel='Cancel'
          confirmLabel='Remove'
          confirmDisabled={isDeleting}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </Box>
  )
}
